import { ref, type Ref } from 'vue'

type ButtonElementGetter = () => HTMLElement | null | undefined
type DirectionGetter = () => 'left' | 'right' | undefined
type EnabledGetter = () => boolean | undefined
type VoidHandler = () => void

interface UseDropdownTeleportPositionOptions {
  direction: DirectionGetter
  enabled: EnabledGetter
  getButtonElement: ButtonElementGetter
}

interface UseDropdownTeleportPositionReturn {
  dropdownStyle: Ref<Record<string, string>>
  dropdownTarget: Ref<HTMLElement>
  onDropdownEnter: VoidHandler
  removeDropdownListeners: VoidHandler
}

const DROPDOWN_GAP = 4

// Nearest ancestor that both scrolls (overflow auto/scroll) and establishes a
// containing block (non-static position), so an absolutely positioned child is
// clipped by it. Plain overflow-hidden containers (e.g. cards) are skipped.
const findScrollableAncestor = (element: HTMLElement | null): HTMLElement | null => {
  let node = element?.parentElement ?? null

  while (node) {
    const style = window.getComputedStyle(node)
    const scrolls = style.overflowY === 'auto' || style.overflowY === 'scroll'

    if (scrolls && style.position !== 'static') {
      return node
    }

    node = node.parentElement
  }

  return null
}

/**
 * Positions a teleported dropdown relative to a trigger button. The dropdown is
 * teleported into the nearest scrollable ancestor (skipping clipping containers
 * like a card's overflow-hidden) so the scroll area itself clips it, instead of
 * letting it float over sticky headers. If no such ancestor exists it falls back
 * to a fixed-positioned dropdown teleported to <body>.
 */
export const useDropdownTeleportPosition = (
  options: UseDropdownTeleportPositionOptions,
): UseDropdownTeleportPositionReturn => {
  const dropdownStyle = ref<Record<string, string>>({})
  const dropdownTarget = ref<HTMLElement>(document.body)

  const updateDropdownPosition = (): void => {
    if (!options.enabled()) return

    const buttonEl = options.getButtonElement()
    if (!buttonEl) return

    const rect = buttonEl.getBoundingClientRect()
    const container = dropdownTarget.value

    if (container === document.body) {
      // No scrollable ancestor: anchor to the viewport.
      const horizontalAnchor =
        options.direction() === 'left'
          ? { left: `${rect.left}px` }
          : { right: `${window.innerWidth - rect.right}px` }

      dropdownStyle.value = {
        minWidth: `${rect.width}px`,
        position: 'fixed',
        top: `${rect.bottom + DROPDOWN_GAP}px`,
        zIndex: '50',
        ...horizontalAnchor,
      }

      return
    }

    // Positioned absolutely inside the scroll container so it scrolls along with
    // the content and is clipped by the container's bounds. Offsets are measured
    // from the padding box (clientLeft/clientTop account for the border, and
    // clientWidth excludes a vertical scrollbar).
    const containerRect = container.getBoundingClientRect()
    const paddingLeft = containerRect.left + container.clientLeft
    const paddingTop = containerRect.top + container.clientTop
    const paddingRight = paddingLeft + container.clientWidth

    const horizontalAnchor =
      options.direction() === 'left'
        ? { left: `${rect.left - paddingLeft + container.scrollLeft}px` }
        : { right: `${paddingRight - rect.right}px` }

    dropdownStyle.value = {
      minWidth: `${rect.width}px`,
      position: 'absolute',
      top: `${rect.bottom - paddingTop + container.scrollTop + DROPDOWN_GAP}px`,
      zIndex: '10',
      ...horizontalAnchor,
    }
  }

  const onDropdownEnter = (): void => {
    dropdownTarget.value =
      findScrollableAncestor(options.getButtonElement() ?? null) ?? document.body
    updateDropdownPosition()
    window.addEventListener('scroll', updateDropdownPosition, true)
    window.addEventListener('resize', updateDropdownPosition)
  }

  const removeDropdownListeners = (): void => {
    window.removeEventListener('scroll', updateDropdownPosition, true)
    window.removeEventListener('resize', updateDropdownPosition)
  }

  return { dropdownStyle, dropdownTarget, onDropdownEnter, removeDropdownListeners }
}
